import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import apiClient from '../../../services/apiClient';

interface Subscriber {
  id: number;
  email: string;
  source: string;
  subscribed_at: string;
}

interface Send {
  id: number;
  send_date: string;
  recipients: number;
  picks_count: number;
  sent_at: string;
}

const NewsletterAdmin: React.FC = () => {
  const [subs, setSubs] = useState<Subscriber[]>([]);
  const [sends, setSends] = useState<Send[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendMsg, setSendMsg] = useState('');

  useEffect(() => {
    Promise.all([
      apiClient.get('/api/admin/newsletter/subscribers').then((r) => r.data),
      apiClient.get('/api/admin/newsletter/sends').then((r) => r.data).catch(() => []),
    ])
      .then(([subData, sendData]) => { setSubs(subData); setSends(sendData); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const handleSendNow = async () => {
    setSending(true);
    setSendMsg('');
    try {
      const { data } = await apiClient.post('/api/admin/newsletter/send-now');
      setSendMsg(data.message || 'Sent!');
    } catch (e: any) {
      setSendMsg(e.message || 'Failed');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const daysAgo = (ts: string) => {
    const days = Math.round((Date.now() - new Date(ts).getTime()) / 86400000);
    if (days === 0) return 'today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Newsletter</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {sendMsg && <Typography variant="caption" color="text.secondary">{sendMsg}</Typography>}
          <Button
            variant="contained"
            size="small"
            startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
            onClick={handleSendNow}
            disabled={sending}
          >
            Send Now
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Subscribers */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Subscribers ({subs.length})
      </Typography>
      {subs.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>No subscribers yet.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>Email</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Signed Up</TableCell>
                <TableCell>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subs.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{s.email}</Typography></TableCell>
                  <TableCell>{s.source}</TableCell>
                  <TableCell>{formatDate(s.subscribed_at)}</TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{daysAgo(s.subscribed_at)}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Send History */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Send History ({sends.length})
      </Typography>
      {sends.length === 0 ? (
        <Alert severity="info">No newsletters sent yet. Click "Send Now" or wait for the 8:30 AM ET daily send.</Alert>
      ) : (
        <TableContainer component={Paper} elevation={1}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700 } }}>
                <TableCell>Date</TableCell>
                <TableCell align="right">Picks</TableCell>
                <TableCell>Sent At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sends.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell>{s.send_date}</TableCell>
                  <TableCell align="right">{s.picks_count}</TableCell>
                  <TableCell>{formatDate(s.sent_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default NewsletterAdmin;
